import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { db } from '@/lib/firebase/config';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { Loader2, X, Image as ImageIcon } from 'lucide-react';

interface StrategyData {
  vision: { ar: string; en: string };
  mission: { ar: string; en: string };
  goals: { ar: string; en: string };
  strategicMapImage?: string;
}

export function StrategySection() {
  const [strategyData, setStrategyData] = useState<StrategyData>({
    vision: { ar: '', en: '' },
    mission: { ar: '', en: '' },
    goals: { ar: '', en: '' },
    strategicMapImage: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchStrategyData();
  }, []);

  const fetchStrategyData = async () => {
    try {
      const docRef = doc(db, 'landing_profile_sections', 'strategy');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setStrategyData({
          vision: typeof data.vision === 'string' ? { ar: data.vision, en: '' } : (data.vision || { ar: '', en: '' }),
          mission: typeof data.mission === 'string' ? { ar: data.mission, en: '' } : (data.mission || { ar: '', en: '' }),
          goals: typeof data.goals === 'string' ? { ar: data.goals, en: '' } : (data.goals || { ar: '', en: '' }),
          strategicMapImage: data.strategicMapImage || '',
        });
      }
    } catch (error) {
      console.error('Error fetching strategy data:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل بيانات الإستراتيجية',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!strategyData.vision.ar.trim() && !strategyData.mission.ar.trim() && !strategyData.goals.ar.trim()) {
      toast({
        title: 'خطأ',
        description: 'يجب إدخال الرؤية أو الرسالة أو الأهداف على الأقل',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const docRef = doc(db, 'landing_profile_sections', 'strategy');
      await updateDoc(docRef, {
        vision: strategyData.vision,
        mission: strategyData.mission,
        goals: strategyData.goals,
        strategicMapImage: strategyData.strategicMapImage?.trim() || '',
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'تم الحفظ',
        description: 'تم تحديث بيانات الإستراتيجية بنجاح',
      });
    } catch (error) {
      console.error('Error saving strategy data:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في حفظ البيانات',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#7dda55]" />
      </div>
    );
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Vision */}
        <div className="space-y-3">
          <Label className="text-lg font-semibold">الرؤية / Vision</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vision-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Textarea
                id="vision-ar"
                value={strategyData.vision.ar}
                onChange={(e) => setStrategyData({ ...strategyData, vision: { ...strategyData.vision, ar: e.target.value } })}
                placeholder="أن نكون المؤسسة الرائدة في تعزيز نمط الحياة الصحي..."
                className="min-h-[120px] font-arabic"
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vision-en" className="text-sm text-muted-foreground">English</Label>
              <Textarea
                id="vision-en"
                value={strategyData.vision.en}
                onChange={(e) => setStrategyData({ ...strategyData, vision: { ...strategyData.vision, en: e.target.value } })}
                placeholder="To be the leading organization in promoting a healthy lifestyle..."
                className="min-h-[120px]"
                dir="ltr"
              />
            </div>
          </div>
        </div>

        {/* Mission */}
        <div className="space-y-3">
          <Label className="text-lg font-semibold">الرسالة / Mission</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mission-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Textarea
                id="mission-ar"
                value={strategyData.mission.ar}
                onChange={(e) => setStrategyData({ ...strategyData, mission: { ...strategyData.mission, ar: e.target.value } })}
                placeholder="نسعى لبناء مجتمع صحي مستدام..."
                className="min-h-[120px] font-arabic"
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mission-en" className="text-sm text-muted-foreground">English</Label>
              <Textarea
                id="mission-en"
                value={strategyData.mission.en}
                onChange={(e) => setStrategyData({ ...strategyData, mission: { ...strategyData.mission, en: e.target.value } })}
                placeholder="We strive to build a sustainable healthy community..."
                className="min-h-[120px]"
                dir="ltr"
              />
            </div>
          </div>
        </div>

        {/* Goals */}
        <div className="space-y-3">
          <Label className="text-lg font-semibold">الأهداف / Goals</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="goals-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Textarea
                id="goals-ar"
                value={strategyData.goals.ar}
                onChange={(e) => setStrategyData({ ...strategyData, goals: { ...strategyData.goals, ar: e.target.value } })}
                placeholder="1. نشر الوعي الصحي في المجتمع&#10;2. تطوير برامج مبتكرة..."
                className="min-h-[200px] font-arabic"
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="goals-en" className="text-sm text-muted-foreground">English</Label>
              <Textarea
                id="goals-en"
                value={strategyData.goals.en}
                onChange={(e) => setStrategyData({ ...strategyData, goals: { ...strategyData.goals, en: e.target.value } })}
                placeholder="1. Spreading health awareness in the community&#10;2. Developing innovative programs..."
                className="min-h-[200px]"
                dir="ltr"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            استخدم الأرقام أو النقاط لتنظيم الأهداف
          </p>
        </div>

        {/* Strategic Map Image */}
        <div className="space-y-2">
          <Label htmlFor="strategicMap" className="text-lg font-semibold">الخريطة الإستراتيجية</Label>
          <p className="text-sm text-gray-600 mb-2">
            أدخل رابط صورة الخريطة الإستراتيجية (اختياري)
          </p>
          <div className="flex gap-2">
            <Input
              id="strategicMap"
              type="url"
              value={strategyData.strategicMapImage}
              onChange={(e) => setStrategyData({ ...strategyData, strategicMapImage: e.target.value })}
              placeholder="https://example.com/strategic-map.png"
              className="flex-1"
              dir="ltr"
            />
            {strategyData.strategicMapImage && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => setStrategyData({ ...strategyData, strategicMapImage: '' })}
                type="button"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Image Preview */}
          {strategyData.strategicMapImage && (
            <div className="border rounded-lg p-4 bg-gray-50 mt-3">
              <p className="text-sm font-medium mb-3 text-gray-700">معاينة الخريطة:</p>
              <div className="relative w-full bg-white rounded border overflow-hidden">
                <img
                  src={strategyData.strategicMapImage}
                  alt="Strategic Map Preview"
                  className="w-full h-auto"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <div className="hidden flex items-center justify-center h-64 bg-gray-100 text-gray-400">
                  <div className="text-center">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                    <p className="text-sm">فشل تحميل الصورة</p>
                    <p className="text-xs">تحقق من صحة الرابط</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#7dda55] hover:bg-[#6bc444]"
          >
            {saving ? (
              <>
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                جاري الحفظ...
              </>
            ) : (
              'حفظ التغييرات'
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}

